/** Shared Other Amount modal calculations (wtTotal / wtPP fields). */

export function calculateOtherAmountTotalCt(entry) {
  const ct = parseFloat(entry?.ct);
  if (isNaN(ct)) return 0;
  return ct;
}

/** Total weight for WT rate: explicit Total (wtTotal) XOR per-piece (wtPP/wt) × Pcs. */
export function calculateOtherAmountTotalWt(entry) {
  const tStr = entry?.wtTotal;
  const hasTotal =
    tStr !== undefined &&
    tStr !== null &&
    String(tStr).trim() !== "" &&
    Number.isFinite(parseFloat(tStr));

  if (hasTotal) {
    const t = parseFloat(tStr);
    return Number.isFinite(t) ? t : 0;
  }

  const pcs = parseFloat(entry?.pcs);
  const wt = parseFloat(entry?.wtPP ?? entry?.wt);
  if (isNaN(pcs) || isNaN(wt)) return 0;
  return pcs * wt;
}

export function calculateOtherAmountLessWeight(entry) {
  const totalWt = calculateOtherAmountTotalWt(entry);
  if (!totalWt || isNaN(totalWt)) return 0;

  const conPercentage = parseFloat(entry?.conPercentage);
  if (!isNaN(conPercentage) && conPercentage > 0) {
    return totalWt * (conPercentage / 100);
  }

  return totalWt;
}

export function calculateOtherAmountAmount(entry) {
  if (!entry?.rate || isNaN(entry.rate)) return 0;

  const rate = parseFloat(entry.rate);
  const rateOn = entry.rateOn?.toLowerCase() || "";
  let amount = 0;

  switch (rateOn) {
    case "fixed":
      amount = rate;
      break;
    case "pcs":
      amount =
        entry.pcs && !isNaN(entry.pcs) ? rate * parseFloat(entry.pcs) : 0;
      break;
    case "ct": {
      const totalCt = calculateOtherAmountTotalCt(entry);
      amount = totalCt ? rate * totalCt : 0;
      break;
    }
    case "wt": {
      const totalWt = calculateOtherAmountTotalWt(entry);
      amount = totalWt ? rate * totalWt : 0;
      break;
    }
    default:
      amount = 0;
  }

  return amount;
}

export function sumOtherAmountEntries(entries) {
  if (!Array.isArray(entries)) return 0;
  return entries.reduce(
    (sum, entry) => sum + (parseFloat(entry?.amount) || 0),
    0,
  );
}

/** API create payload for one other-charge line (camelCase). */
export function mapOtherAmountEntryForCreate(entry) {
  if (!entry) return null;
  const wtPP = entry.wtPP ?? entry.wt ?? "";
  return {
    chargeType: entry.chargeType ?? "",
    pcs: entry.pcs ?? "",
    ct: entry.ct ?? "",
    wtPP,
    wtTotal: entry.wtTotal ?? "",
    conPercentage: entry.conPercentage ?? "",
    rateOn: entry.rateOn ?? "",
    rate: entry.rate ?? "",
    amount: entry.amount ?? "",
  };
}

export function mapOtherAmountEntriesForCreate(entries) {
  if (!Array.isArray(entries)) return [];
  return entries.map(mapOtherAmountEntryForCreate).filter(Boolean);
}

/** API update payload — existing lines keep `id`, new lines use `id: null`. */
export function mapOtherAmountEntryForUpdate(entry) {
  const base = mapOtherAmountEntryForCreate(entry);
  if (!base) return null;
  const id = entry?.id;
  return {
    id: id != null && id !== "" ? id : null,
    ...base,
  };
}

export function mapOtherAmountEntriesForUpdate(entries) {
  if (!Array.isArray(entries)) return [];
  return entries.map(mapOtherAmountEntryForUpdate).filter(Boolean);
}

/** Map API charge row → UI `otherAmountEntries` shape. */
export function mapApiChargeToOtherAmountEntry(charge) {
  if (!charge) return null;

  const isPresent = (v) =>
    v !== undefined && v !== null && String(v).trim() !== "";

  // Do not fall back PP ← wt: API `wt` is total weight when wt_pp is null
  const wtPPRaw = charge.wtPP ?? charge.wt_pp;
  const wtTotalRaw = charge.wtTotal ?? charge.wt_total;
  const wtFallback = charge.wt ?? charge.weight;

  const hasPP = isPresent(wtPPRaw);
  const hasTotal = isPresent(wtTotalRaw);

  let wtPP = hasPP ? wtPPRaw : "";
  let wtTotal = hasTotal ? wtTotalRaw : "";

  // Legacy rows that only send `wt` (treat as Total, not PP)
  if (!hasPP && !hasTotal && isPresent(wtFallback)) {
    wtTotal = wtFallback;
  }

  return {
    id: charge.id ?? undefined,
    chargeType: charge.chargeType ?? charge.charge_type ?? "",
    pcs: charge.pcs ?? "",
    ct: charge.ct ?? "",
    wtPP,
    wtTotal,
    wt: hasPP ? wtPP : "",
    conPercentage: charge.conPercentage ?? charge.con_percent ?? "",
    rateOn: charge.rateOn ?? charge.rate_on ?? "",
    rate: charge.rate ?? "",
    amount: charge.amount ?? charge.amt ?? "",
  };
}

export function mapApiChargesToOtherAmountEntries(charges) {
  if (!Array.isArray(charges)) return [];
  return charges.map(mapApiChargeToOtherAmountEntry).filter(Boolean);
}
